import { currentLoggedRelyingParty } from '@/shared/api/apollo/cache';
import type { FeatureFlag, RelyingPartySettingType } from '@/shared/lib/types/relyingParty';
import { create } from 'zustand';

export interface FeatureFlags {
  [FeatureFlag.SHOW_CHART_REVIEW]: boolean;
  [FeatureFlag.SHOW_AI_NOTES_SUMMARY]: boolean;
  [FeatureFlag.PLAYFUL_FEEDBACK]: boolean;
  [FeatureFlag.AI_SEARCH]: boolean;
  [FeatureFlag.ENGAGEMENT_TIME_TRACKING]: boolean;
  [FeatureFlag.CCM_ONLY_ENROLLMENT]: boolean;
}

export interface FeatureFlagStore {
  flags: FeatureFlags;
  initializeFlags: (settings: RelyingPartySettingType) => void;
  isFeatureEnabled: (flagName: FeatureFlag) => boolean;
}

const flagsFrom = (settings: RelyingPartySettingType): FeatureFlags => ({
  chartReview:
    settings?.AdditionalFeatures?.chartReview !== undefined
      ? settings?.AdditionalFeatures?.chartReview === 'true' || settings?.AdditionalFeatures?.chartReview === true
      : true,
  aiNotesSummary: settings?.AdditionalFeatures?.aiNotesSummary === 'true' || settings?.AdditionalFeatures?.aiNotesSummary === true,
  playfulFeedbackEnabled:
    settings?.AdditionalFeatures?.playfulFeedbackEnabled === 'true' || settings?.AdditionalFeatures?.playfulFeedbackEnabled === true,
  aiSearch: settings?.AdditionalFeatures?.aiSearch === 'true' || settings?.AdditionalFeatures?.aiSearch === true,
  engagementTimeTracking:
    settings?.AdditionalFeatures?.engagementTimeTracking === 'true' || settings?.AdditionalFeatures?.engagementTimeTracking === true,
  ccmOnlyEnrollment: settings?.AdditionalFeatures?.ccmOnlyEnrollment === 'true' || settings?.AdditionalFeatures?.ccmOnlyEnrollment === true
});

// Seeded from the pharmacy saved at login, so the first render after a reload doesn't see every flag off.
const initialFlags = (): FeatureFlags => {
  const settings = currentLoggedRelyingParty()?.settings;
  if (settings) {
    try {
      return flagsFrom(JSON.parse(settings));
    } catch {
      // Unreadable settings fall through to the defaults, as the settings provider does.
    }
  }
  return {
    chartReview: false,
    aiNotesSummary: false,
    playfulFeedbackEnabled: false,
    aiSearch: false,
    engagementTimeTracking: false,
    ccmOnlyEnrollment: false
  };
};

export const useFeatureFlagStore = create<FeatureFlagStore>((set, get) => ({
  flags: initialFlags(),
  initializeFlags: (settings: RelyingPartySettingType) => set({ flags: flagsFrom(settings) }),
  isFeatureEnabled: flagName => get().flags[flagName]
}));
